import { parseISO } from 'date-fns';
import { type Static, type TProperties, type TSchema, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';

export const Nullable = <T extends TSchema>(schema: T) =>
  Type.Union([schema, Type.Null()]);

export const OptionalNullable = <T extends TSchema>(schema: T) =>
  Type.Optional(Type.Union([Nullable(schema), Type.Undefined()]));

export const ReadonlyObject = <Properties extends TProperties>(
  properties: Properties,
) => Type.ReadonlyType(Type.Object(properties));

export const ReadonlyInterface = <
  Heritage extends TSchema[],
  Properties extends TProperties,
>(
  heritage: [...Heritage],
  properties: Properties,
) => Type.ReadonlyType(Type.Interface(heritage, properties));

export const ReadonlyArray = <Type extends TSchema>(items: Type) =>
  Type.Immutable(Type.Array(items));

export const DateTimeTransform = Type.Codec(Type.String())
  .Decode((value) => parseISO(value))
  .Encode((value) => value.toISOString());

export type LocString = Static<typeof LocString>;
export const LocString = Type.Record(
  Type.String(),
  Type.Union([Type.String(), Type.Null(), Type.Undefined()]),
);

const LocStringChecker = TypeCompiler.Compile(LocString);
export function isLocString(value: unknown): value is LocString {
  return LocStringChecker.Check(value);
}

export function isScalar(
  value: unknown,
): value is string | number | null | undefined {
  return (
    value == null || typeof value === 'string' || typeof value === 'number'
  );
}

export function getFromLocString(
  locStr: LocString | undefined,
  languageCode: string,
): string | undefined {
  if (locStr == null) {
    return undefined;
  }

  const val = locStr[languageCode];
  if (val == null) {
    return undefined;
  }
  return val;
}

export type FieldValue = LocString | string | boolean | number | null;

export interface ExtraInfo {
  version?: number;
  was_overruled: boolean;
  was_recategorized?: boolean;
  intended_value: FieldValue;
  update_value?: FieldValue;
  input_language_code?: string;
}
